import { Modal, Form, Input, Button, message, Checkbox } from "antd";
import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { API } from "../../../config/API";
import { POST } from "../../../utils/apirequest";

interface ResetPasswordModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  user: {
    id: number;
    full_name: string;
    email: string;
  } | null;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  onCancel,
  onSubmit,
  user,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [useDefaultPassword, setUseDefaultPassword] = useState(false);
  const [pwValue, setPwValue] = useState("");

  const passwordRules = [
    { label: "Minimum 8 characters", test: (v: string) => v.length >= 8 },
    { label: "At least one lowercase letter", test: (v: string) => /[a-z]/.test(v) },
    { label: "At least one uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
    { label: "At least one number", test: (v: string) => /\d/.test(v) },
    { label: "At least one special character", test: (v: string) => /[!@#$%^&*()_+[\]{};':"\\|,.<>?]/.test(v) },
  ];

  const allPwRulesPassed = pwValue ? passwordRules.every((r) => r.test(pwValue)) : false;

  useEffect(() => {
    if (open) {
      form.resetFields();
      setUseDefaultPassword(false);
      setPwValue("");
    }
  }, [open, form]);

  useEffect(() => {
    if (useDefaultPassword) {
      form.setFieldsValue({ password: process.env.REACT_APP_DEFAULT_PASSWORD });
      // Trigger validation after setting the value
      form.validateFields(["password"]).catch(() => {
        // Validation errors are handled by the form
      });
    } else {
      form.setFieldsValue({ password: "" });
      setPwValue("");
      form.setFields([{ name: "password", errors: [] }]);
    }
  }, [useDefaultPassword, form]);

  const handleSubmit = async (values: any) => {
    if (!user) {
      message.error("User information is missing");
      return;
    }

    // Validate password length before submitting
    const password = useDefaultPassword
      ? process.env.REACT_APP_DEFAULT_PASSWORD
      : values.password;

    if (!password || password.length < 8) {
      message.error("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {

      const payload = {
        data: {
          userId: user.id,
          password: password,
        },
      };

      await POST(API.EPAPER_USERS_ADMIN_RESET_PASSWORD, payload);

      message.success("Password reset successfully!");
      form.resetFields();
      setUseDefaultPassword(false);
      onSubmit();
      onCancel();
    } catch (error: any) {
      console.error("Reset password error:", error);
      const errorMessage =
        error?.data?.error?.message ||
        error?.error?.message ||
        "Failed to reset password";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setUseDefaultPassword(false);
    setPwValue("");
    onCancel();
  };

  return (
    <Modal
      width="95%"
      style={{ maxWidth: 600, top: 20 }}
      title="Reset Password"
      open={open}
      onCancel={handleCancel}
      footer={null}
    >
      <br />
      {user && (
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>User:</strong> {user.full_name} ({user.email})
          </p>
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          password: "",
        }}
      >
        <Form.Item
          label="New Password"
          name="password"
          rules={[
            {
              validator: (_: any, value: any) => {
                if (!value) return Promise.reject("Password is required");
                if (passwordRules.every((r) => r.test(value))) return Promise.resolve();
                return Promise.reject("");
              },
            },
          ]}
          style={pwValue && !allPwRulesPassed ? { marginBottom: 4 } : {}}
          help={pwValue && !allPwRulesPassed ? "" : undefined}
        >
          <Input.Password
            placeholder="Enter new password"
            size="large"
            onChange={(e) => setPwValue(e.target.value)}
            disabled={useDefaultPassword}
          />
        </Form.Item>
        {pwValue && !allPwRulesPassed && (
          <div style={{ marginBottom: 12 }}>
            {passwordRules.map((rule, i) => {
              const passed = rule.test(pwValue);
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "2px 0",
                    fontSize: 13,
                    color: passed ? "#52c41a" : "#999",
                  }}
                >
                  <span style={{ fontSize: 14 }}>{passed ? "✓" : "✕"}</span>
                  {rule.label}
                </div>
              );
            })}
          </div>
        )}

        <Form.Item>
          <Checkbox
            checked={useDefaultPassword}
            onChange={(e) => setUseDefaultPassword(e.target.checked)}
          >
            Use Default Password: <strong>{process.env.REACT_APP_DEFAULT_PASSWORD}</strong>
          </Checkbox>
        </Form.Item>

        <br />

        <Row>
          <Col md={6}>
            <Form.Item>
              <Button onClick={handleCancel} size="large" block>
                Cancel
              </Button>
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item>
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                disabled={!allPwRulesPassed}
              >
                Submit
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ResetPasswordModal;




